import React, { useState } from "react";
import { storyAge, storyBefore } from "./lead/leadModel.mjs";

/*
 * THE STORY (frames 1a, 1d, 1g): two to four sentences ending on a bold "Where it
 * stands:", with its age ("written 09:39 today") and the rewrite ↻. Since PR #155
 * it is one story per COMPANY, read off everyone's timeline plus the won date, the
 * signed terms, the agreement notice and what is owed, so it no longer contradicts
 * the record around it.
 *
 *   - "BEFORE THE WELCOME" (frame 1a): when something has happened since the story
 *     last read the record, the meta says what, from the payload's newest_at
 *     against the timeline (leadModel.storyBefore: the welcome and the finance intro
 *     by name, else whose email, the call, the move). On a parked company's wake-up
 *     day a story written before it says "before the wake-up".
 *   - "Couldn’t rewrite just now, showing the last version" when a rewrite failed:
 *     the server keeps the cached text and marks it stale, or the request itself
 *     threw. The old text stays on screen either way.
 *   - NONE UNDER THREE TIMELINE ENTRIES: the shell leaves the section out
 *     (situation.mjs showStory), and the server answers story:null (thin) itself,
 *     which renders nothing here: a missing story is never an empty card.
 *
 * Props:
 *   record   the shell's bundle: story ({ story, generated_at, newest_at, stale,
 *            thin } | null | undefined while loading), timeline.rows, act
 *            (onboarding), company (recycle_until, status), contact, now
 *   ops      ops.rewriteStory() → the new payload
 */
const WHERE = /(^|\s)(Where it stands:\s*)/;

function render(text) {
  const m = WHERE.exec(text);
  if (!m) return text;
  const cut = m.index + m[1].length;
  return (
    <>
      {text.slice(0, cut)}
      <b>{m[2].trimEnd()}</b> {text.slice(cut + m[2].length)}
    </>
  );
}

export default function StoryCard({ record, ops }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // What was on screen when a rewrite came back empty: the card keeps it.
  const [kept, setKept] = useState(null);
  const s = record.story && record.story.story ? record.story : kept;
  if (!s || !s.story) return null;
  const now = record.now || new Date();
  const co = record.company || {};
  const age = storyAge(s, { now });
  const before = storyBefore(s, record.timeline.rows, {
    company: record.company,
    contact: record.contact,
    onboarding: record.act && record.act.onboarding,
    wakesAt: co.status === "recycled" ? co.recycle_until : null,
    now,
  });

  const rewrite = async () => {
    setBusy(true);
    setFailed(false);
    setKept(s);
    try {
      const fresh = await ops.rewriteStory();
      if (!fresh || !fresh.story || fresh.stale || fresh.failed) setFailed(true);
      else setKept(null);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rec-card rec-story" aria-label="The story">
      <div className="rec-card__head rec-story__head">
        <span className="rec-label">The story</span>
        <span className="rec-story__meta">
          {busy ? "rewriting…" : [age, before].filter(Boolean).join(" · ")}
          <button type="button" className="rec-iconbtn" title="Rewrite" aria-label="Rewrite the story" disabled={busy} onClick={rewrite}>
            ↻
          </button>
        </span>
      </div>
      <p className="rec-story__text">{render(s.story)}</p>
      {(s.stale || failed) && <div className="rec-meta">Couldn’t rewrite just now, showing the last version.</div>}
    </section>
  );
}
